import React, { useState, useEffect } from 'react';
import { FiBriefcase, FiUsers, FiCheck, FiCheckSquare, FiSquare } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jobApi } from '../../services/api';
import { getAllMentees, assignJobsToMentees } from '../../services/mentorService';
import PageHeader from '../../components/layout/PageHeader';
import { pageDescriptions } from '../../data/pageDescriptions';

const JobAssignments = () => {
  const [jobs, setJobs] = useState([]);
  const [mentees, setMentees] = useState([]);
  const [selectedJobs, setSelectedJobs] = useState([]);
  const [selectedMentees, setSelectedMentees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [jobsResponse, menteesResponse] = await Promise.all([
        jobApi.getAllJobs(),
        getAllMentees()
      ]);
      
      // Create a map of job assignments from mentees' job data
      const jobAssignments = {};
      menteesResponse.data.forEach(mentee => {
        if (mentee.assigned_jobs) {
          mentee.assigned_jobs.forEach(jobId => {
            if (!jobAssignments[jobId]) {
              jobAssignments[jobId] = [];
            }
            jobAssignments[jobId].push(mentee.full_name);
          });
        }
      });
      
      // Add assignment info to jobs
      const jobsWithAssignments = jobsResponse.data.map(job => ({
        ...job,
        assignedTo: jobAssignments[job.id] || []
      }));
      
      setJobs(jobsWithAssignments);
      setMentees(menteesResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleJobToggle = (jobId) => {
    setSelectedJobs(prev => 
      prev.includes(jobId) 
        ? prev.filter(id => id !== jobId)
        : [...prev, jobId]
    );
  };

  const handleMenteeToggle = (menteeId) => {
    setSelectedMentees(prev => 
      prev.includes(menteeId) 
        ? prev.filter(id => id !== menteeId)
        : [...prev, menteeId]
    );
  };

  const handleSelectAllJobs = () => {
    if (selectedJobs.length === jobs.length) {
      setSelectedJobs([]);
    } else {
      setSelectedJobs(jobs.map(job => job.id));
    }
  };

  const handleSelectAllMentees = () => {
    if (selectedMentees.length === mentees.length) {
      setSelectedMentees([]);
    } else {
      setSelectedMentees(mentees.map(mentee => mentee.id));
    }
  };

  const handleAssign = async () => {
    if (selectedJobs.length === 0) {
      toast.error('Please select at least one job');
      return;
    }
    if (selectedMentees.length === 0) {
      toast.error('Please select at least one mentee');
      return;
    }

    try {
      setAssigning(true);
      const response = await assignJobsToMentees(selectedJobs, selectedMentees);
      toast.success(response.data.message);
      setSelectedJobs([]);
      setSelectedMentees([]);
    } catch (error) {
      console.error('Error assigning jobs:', error);
      toast.error('Failed to assign jobs');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  const copy = pageDescriptions['/mentor/job-assignments'];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <PageHeader
          title={copy.title}
          description={copy.description}
          whatYouCanDo={copy.whatYouCanDo}
          whatYouShouldDo={copy.whatYouShouldDo}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Jobs Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              <FiBriefcase className="inline h-5 w-5 mr-2" />
              Select Jobs ({selectedJobs.length} selected)
            </h2>
            <button
              onClick={handleSelectAllJobs}
              className="flex items-center text-sm text-primary-600 hover:text-primary-800"
            >
              {selectedJobs.length === jobs.length ? (
                <FiCheckSquare className="h-4 w-4 mr-1" />
              ) : (
                <FiSquare className="h-4 w-4 mr-1" />
              )}
              Select All
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
            {jobs.map(job => (
              <label key={job.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                <input
                  type="checkbox"
                  checked={selectedJobs.includes(job.id)}
                  onChange={() => handleJobToggle(job.id)}
                  className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{job.title}</div>
                  <div className="text-sm text-gray-500">{job.company}</div>
                  {job.assignedTo.length > 0 && (
                    <div className="text-xs text-blue-600 mt-1">
                      Assigned to: {job.assignedTo.join(', ')}
                    </div>
                  )}
                </div>
              </label>
            ))}
          </div>
        </div>

        {/* Mentees Selection */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              <FiUsers className="inline h-5 w-5 mr-2" />
              Select Mentees ({selectedMentees.length} selected)
            </h2>
            <button
              onClick={handleSelectAllMentees}
              className="flex items-center text-sm text-primary-600 hover:text-primary-800"
            >
              {selectedMentees.length === mentees.length ? (
                <FiCheckSquare className="h-4 w-4 mr-1" />
              ) : (
                <FiSquare className="h-4 w-4 mr-1" />
              )}
              Select All
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto border border-gray-300 rounded-md">
            {mentees.map(mentee => (
              <label key={mentee.id} className="flex items-center p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0">
                <input
                  type="checkbox"
                  checked={selectedMentees.includes(mentee.id)}
                  onChange={() => handleMenteeToggle(mentee.id)}
                  className="mr-3 h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{mentee.full_name}</div>
                  <div className="text-sm text-gray-500">{mentee.email}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Assignment Action */}
      <div className="mt-8 flex justify-center">
        <button
          onClick={handleAssign}
          disabled={assigning || selectedJobs.length === 0 || selectedMentees.length === 0}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400"
        >
          <FiCheck className="mr-2 h-5 w-5" />
          {assigning ? 'Assigning...' : `Assign ${selectedJobs.length} Job(s) to ${selectedMentees.length} Mentee(s)`}
        </button>
      </div>
    </div>
  );
};

export default JobAssignments;